/**
 * What happens to Shadygram built-ins that ISF has no equivalent for.
 *
 * Two honest tiers, and the difference matters:
 *
 *  - **Scalars degrade cleanly.** `u_mouthOpen`, `u_faceScale`, the device
 *    sensors and so on become ordinary ISF `float` INPUTs. A VJ drives them by
 *    hand or by MIDI and the shader behaves sensibly.
 *
 *  - **Data textures do not.** `u_faceLandmarks`, `u_poseLandmarks` and the
 *    segmentation masks carry hundreds of points; no slider substitutes for
 *    them. They become neutral constants and the conversion reports a warning,
 *    so the file is valid ISF that runs, but the author is told plainly that it
 *    will not look right in another host. Pretending otherwise would ship
 *    silently-wrong output to a stage.
 */

import type { ISFInput } from '../types';

export interface ScalarSubstitution {
  /** The ISF input synthesised in place of the built-in. */
  input: ISFInput;
  /** Text used to explain the substitution in DESCRIPTION. */
  note: string;
}

/** Built-in float uniforms that become plain sliders. */
const SCALAR_INPUTS: Record<string, { label: string; min: number; max: number; def: number }> = {
  u_faceDetected: { label: 'Face Detected', min: 0, max: 1, def: 1 },
  u_faceScale: { label: 'Face Scale', min: 0, max: 1, def: 0.3 },
  u_mouthOpen: { label: 'Mouth Open', min: 0, max: 1, def: 0 },
  u_mouthSmile: { label: 'Mouth Smile', min: 0, max: 1, def: 0 },
  u_mouthPucker: { label: 'Mouth Pucker', min: 0, max: 1, def: 0 },
  u_cheekPuff: { label: 'Cheek Puff', min: 0, max: 1, def: 0 },
  u_eyeBlinkLeft: { label: 'Blink Left', min: 0, max: 1, def: 0 },
  u_eyeBlinkRight: { label: 'Blink Right', min: 0, max: 1, def: 0 },
  u_eyebrowLeftUp: { label: 'Brow Left', min: 0, max: 1, def: 0 },
  u_eyebrowRightUp: { label: 'Brow Right', min: 0, max: 1, def: 0 },
  u_handDetected: { label: 'Hand Detected', min: 0, max: 1, def: 0 },
  u_handCount: { label: 'Hand Count', min: 0, max: 2, def: 0 },
  u_poseDetected: { label: 'Pose Detected', min: 0, max: 1, def: 0 },
  u_surfaceFound: { label: 'Surface Found', min: 0, max: 1, def: 0 },
  u_faceLandmarkCount: { label: 'Face Landmarks', min: 0, max: 478, def: 478 },
  u_handLandmarkCount: { label: 'Hand Landmarks', min: 0, max: 21, def: 21 },
  u_poseLandmarkCount: { label: 'Pose Landmarks', min: 0, max: 33, def: 33 },
  u_segmentMulticlass: { label: 'Multiclass Segmentation', min: 0, max: 1, def: 0 },
};

/** Built-in vec3 uniforms, exploded into three sliders. */
const VECTOR_INPUTS: Record<string, { label: string; min: number; max: number; def: [number, number, number] }> = {
  u_facePosition: { label: 'Face Position', min: 0, max: 1, def: [0.5, 0.5, 0] },
  u_faceRotation: { label: 'Face Rotation', min: -3.15, max: 3.15, def: [0, 0, 0] },
  u_deviceOrientation: { label: 'Device Orientation', min: -3.15, max: 3.15, def: [0, 0, 0] },
  u_deviceAcceleration: { label: 'Device Acceleration', min: -20, max: 20, def: [0, 0, 0] },
  u_gravity: { label: 'Gravity', min: -10, max: 10, def: [0, -9.81, 0] },
  u_cameraPosition: { label: 'Camera Position', min: -10, max: 10, def: [0, 0, 0] },
  u_surfaceHit: { label: 'Surface Hit', min: -10, max: 10, def: [0, 0, 0] },
  u_surfaceNormal: { label: 'Surface Normal', min: -1, max: 1, def: [0, 1, 0] },
};

/**
 * Built-ins and helpers with no honest substitute. The replacement is chosen to
 * be the least destructive value, not merely zero: `getSegmentation` returns 1.0
 * ("everything is the subject") so a background-replacement shader keeps showing
 * its input rather than erasing it.
 */
const NEUTRAL_REPLACEMENTS: Record<string, string> = {
  u_faceLandmarks: 'vec4(0.5)',
  u_handLandmarks: 'vec4(0.5)',
  u_handWorldLandmarks: 'vec4(0.0)',
  u_poseLandmarks: 'vec4(0.5)',
  u_poseWorldLandmarks: 'vec4(0.0)',
  u_segmentationMask: 'vec4(1.0)',
  u_segmentHair: 'vec4(0.0)',
  u_segmentSkin: 'vec4(0.0)',
  u_segmentFace: 'vec4(0.0)',
  u_segmentClothes: 'vec4(0.0)',
  u_cameraMatrix: 'mat4(1.0)',
  u_handHandedness: 'vec2(0.0)',
};

/** Helper calls replaced wholesale, with the value each degrades to. */
export const NEUTRAL_CALLS: Record<string, string> = {
  getFaceLandmark: 'vec3(0.5, 0.5, 0.0)',
  getHandLandmark: 'vec3(0.5, 0.5, 0.0)',
  getHandLandmarkH: 'vec3(0.5, 0.5, 0.0)',
  getPoseLandmark: 'vec3(0.5, 0.5, 0.0)',
  getHandWorldLandmark: 'vec4(0.0)',
  getHandWorldLandmarkH: 'vec4(0.0)',
  getPoseWorldLandmark: 'vec4(0.0)',
  getHandSide: '0.0',
  getSegmentation: '1.0',
  getHairMask: '0.0',
  getSkinMask: '0.0',
  getFaceMask: '0.0',
  getClothesMask: '0.0',
  getBodyDepth: '0.0',
  getHandDepth: '0.0',
};

/** Human-readable names for the warning the author sees. */
export const FIDELITY_CLIFF_NAMES = new Set([
  ...Object.keys(NEUTRAL_REPLACEMENTS),
  ...Object.keys(NEUTRAL_CALLS),
]);

export function scalarSubstitutionFor(name: string): ScalarSubstitution | null {
  const spec = SCALAR_INPUTS[name];
  if (!spec) return null;
  return {
    input: {
      NAME: isfNameFor(name),
      TYPE: 'float',
      LABEL: spec.label,
      DEFAULT: spec.def,
      MIN: spec.min,
      MAX: spec.max,
    },
    note: `${name} → "${isfNameFor(name)}" slider`,
  };
}

export function vectorSubstitutionFor(name: string): { inputs: ISFInput[]; expression: string; note: string } | null {
  const spec = VECTOR_INPUTS[name];
  if (!spec) return null;

  const base = isfNameFor(name);
  const axes = ['x', 'y', 'z'] as const;
  const inputs: ISFInput[] = axes.map((axis, i) => ({
    NAME: `${base}_${axis}`,
    TYPE: 'float',
    LABEL: `${spec.label} ${axis.toUpperCase()}`,
    DEFAULT: spec.def[i],
    MIN: spec.min,
    MAX: spec.max,
  }));

  return {
    inputs,
    expression: `vec3(${base}_x, ${base}_y, ${base}_z)`,
    note: `${name} → three "${spec.label}" sliders`,
  };
}

export function neutralReplacementFor(name: string): string | null {
  return NEUTRAL_REPLACEMENTS[name] ?? null;
}

/** `u_mouthOpen` → `mouthOpen`; ISF names conventionally drop the `u_`. */
export function isfNameFor(uniformName: string): string {
  return uniformName.replace(/^u_/, '');
}
